import { themeGet } from '../../utils/theme-get.js';
import React__default from 'react';
import styled from 'styled-components';
import Box from '../Box/Box.js';
import Link from '../Link/Link.js';
import Text from '../Text/Text.js';
import Spacer from '../Spacer/Spacer.js';
import { Card } from '../Card/Card.js';

const productCategoryGradients = {
  appfire: 'linear-gradient(305.08deg, #1fda96 12.81%, #3fb3f2 97.78%)',
  workflowAutomation: 'linear-gradient(83.16deg, #8AEECA -0.85%, #1DCB8C 100%);',
  publishing: 'linear-gradient(83.16deg, #CFA0DC -0.85%, #974FBF 100%)',
  portfolioManagement: 'linear-gradient(83.16deg, #F29BB6 -0.85%, #FADDE6 64.28%, #E64477 100%)',
  itsm: 'linear-gradient(83.16deg, #CFE7FC -0.85%, #20549A 100%)',
  documentManagement: 'linear-gradient(83.16deg, #BAEA9B -0.85%, #63A83B 100%)',
  developerTools: 'linear-gradient(83.16deg, #F9D95D -0.85%, #FAB34C 64.28%, #C67306 100%)',
  biReporting: 'linear-gradient(83.16deg, #FCB18F -0.85%, #F96C2C 100%)',
  agile: 'linear-gradient(83.16deg, #8AE8EE -0.85%, #0D7EBE 100%)',
  adminTools: 'linear-gradient(83.16deg, #F48E8E -0.85%, #B7226A 99.47%)'
};
const TopBorder = styled(Box).withConfig({
  displayName: "SurfaceCard__TopBorder",
  componentId: "sc-1ixthmu-0"
})(["min-height:11px;width:100.9%;margin-left:-1px;position:absolute;top:0;left:0;border-top-left-radius:", ";border-top-right-radius:", ";border-top:1px solid transparent;background:", ";"], themeGet('radii.3'), themeGet('radii.3'), p => p.productCategory && {}.hasOwnProperty.call(productCategoryGradients, p.productCategory) ? productCategoryGradients[p.productCategory] : `linear-gradient(305.08deg, #1fda96 12.81%, #3fb3f2 97.78%)`);
const StyledSurfaceCard = styled(Card).withConfig({
  displayName: "SurfaceCard__StyledSurfaceCard",
  componentId: "sc-1ixthmu-1"
})(["list-style:none;margin:1em 0;padding:", " ", ";position:relative;max-width:280px;flex:0 1 280px;display:grid;grid-template-rows:auto auto 1fr auto 0.5fr;gap:", ";border-radius:", ";border:1px solid ", ";border-top:", ";border-top-color:", ";box-shadow:", ";transition:350ms;min-height:326px;&:hover{background-color:", ";box-shadow:", ";}&:focus-within{background-color:", ";box-shadow:", ";}&:active{background-color:", ";box-shadow:", ";}"], themeGet('space.5'), themeGet('space.4'), themeGet('space.2'), themeGet('radii.3'), themeGet('colors.border.default'), p => p.hasTopBorder === true ? 'none' : '1px solid', p => p.hasTopBorder === true ? 'none' : themeGet('colors.border.default'), p => p.raised === true ? themeGet('colors.elevation.shadow.raised') : '', themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.elevation.shadow.overlay'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.elevation.shadow.overlay'), themeGet('colors.background.neutral.subtle.pressed'), themeGet('colors.elevation.shadow.overlay'));
const MainLink = styled(Link).withConfig({
  displayName: "SurfaceCard__MainLink",
  componentId: "sc-1ixthmu-2"
})(["color:", ";display:block;z-index:1;padding:4px;&::after{position:absolute;top:0;left:0;right:0;bottom:0;content:' ';}&:hover{color:", ";}&:focus{outline:2px solid ", ";border-radius:", ";color:", ";}grid-row:5 / 6;margin-top:auto;"], themeGet('colors.link.default'), themeGet('colors.link.default'), themeGet('colors.border.focused'), themeGet('radii.1'), themeGet('colors.link.default'));
const StyledHeading = styled(Text).withConfig({
  displayName: "SurfaceCard__StyledHeading",
  componentId: "sc-1ixthmu-3"
})(["font-size:", ";line-height:", ";color:", ";"], themeGet('fontSizes.5'), themeGet('lineHeights.condensed'), themeGet('colors.text.default'));
const HeadlineText = styled(Text).withConfig({
  displayName: "SurfaceCard__HeadlineText",
  componentId: "sc-1ixthmu-4"
})(["font-size:11px;font-weight:700;letter-spacing:0;line-height:", ";text-transform:uppercase;color:", ";"], themeGet('lineHeights.default'), themeGet('colors.text.subtlest'));
const CardBodyContent = styled(Text).withConfig({
  displayName: "SurfaceCard__CardBodyContent",
  componentId: "sc-1ixthmu-5"
})(["color:", ";margin-top:", ";grid-row:3 / 4;"], themeGet('colors.text.subtle'), themeGet('space.3'));

/**
 *
 * Getting Started Surface Card renders a list item or other semantically appropriate card item that has a clickable surface,
 * while maintaining semantic html structure and accessibility features for users of assistive technologies should be provided affordances
 * that ensure that the card and content are usable and inclusive.
 *
 * Typically used in a grid of 3 or 4.
 */
const SurfaceCard = ({
  title,
  headline,
  label,
  url,
  as = 'li',
  content,
  hasTopBorder = false,
  raised = false,
  productCategory = 'appfire'
}) => {
  return /*#__PURE__*/React__default.createElement(StyledSurfaceCard, {
    as: as,
    raised: raised,
    hasTopBorder: hasTopBorder,
    "data-testid": "surface-card-component"
  }, hasTopBorder ? /*#__PURE__*/React__default.createElement(TopBorder, {
    productCategory: productCategory
  }) : null, /*#__PURE__*/React__default.createElement(HeadlineText, {
    as: "p"
  }, headline), /*#__PURE__*/React__default.createElement(StyledHeading, {
    as: "h2"
  }, title), /*#__PURE__*/React__default.createElement(CardBodyContent, {
    as: "p"
  }, content), /*#__PURE__*/React__default.createElement(Spacer, {
    axis: "vertical",
    size: 32
  }), /*#__PURE__*/React__default.createElement(MainLink, {
    href: url
  }, label));
};
SurfaceCard.displayName = "SurfaceCard";
SurfaceCard.displayName = 'SurfaceCard';
var SurfaceCard$1 = SurfaceCard;

export { SurfaceCard$1 as default, productCategoryGradients };
